import type { QuizDefinition, QuizQuestion } from "@quiz/quiz-core";
import { getQuizPresentation } from "@quiz/quiz-definitions";

const assets = new Map<string, { ready: Promise<void>; loaded?: boolean }>();
const fonts = new Map<string, { ready: Promise<void>; settled: boolean }>();

function prepareFonts(family: string): Promise<void> {
  const cached = fonts.get(family);
  if (cached) return cached.ready;
  const entry = { ready: Promise.resolve(), settled: false };
  entry.ready = new Promise<void>((resolve) => {
    const timeout = window.setTimeout(() => finish(false), 15000);
    function finish(loaded: boolean): void {
      if (entry.settled) return;
      window.clearTimeout(timeout);
      entry.settled = true;
      // Keep one stable fallback if a font fails or times out; never swap it mid-question.
      document.documentElement.dataset.comicFonts = loaded ? "ready" : "fallback";
      resolve();
    }
    const sample = "English ABC abc 0123 Українська ҐЄІЇ ґєії";
    void Promise.all([400, 500, 600, 700, 800].map((weight) =>
      document.fonts.load(`${weight} 16px ${family}`, sample)
    )).then(() => finish(true), () => finish(false));
  });
  fonts.set(family, entry);
  return entry.ready;
}

function sources(quiz: QuizDefinition, question: QuizQuestion): string[] {
  const comic = getQuizPresentation(quiz).comicQuest;
  const visual = question.visual;
  return [
    comic?.parchmentSrc,
    comic?.logoSrc,
    visual && (window.matchMedia("(max-width: 600px)").matches ? visual.mobileSrc ?? visual.src : visual.src)
  ].filter((src): src is string => Boolean(src));
}

function prepareImage(src: string): Promise<void> {
  const cached = assets.get(src);
  if (cached) return cached.ready;
  const entry: { ready: Promise<void>; loaded?: boolean } = { ready: Promise.resolve() };
  const image = new Image();
  image.decoding = "async";
  image.fetchPriority = "high";
  entry.ready = new Promise<void>((resolve) => {
    // A failed decorative asset must not prevent answering the quiz.
    const timeout = window.setTimeout(() => finish(false), 15000);
    function finish(loaded: boolean): void {
      if (entry.loaded !== undefined) return;
      window.clearTimeout(timeout);
      entry.loaded = loaded;
      resolve();
    }
    image.src = src;
    void image.decode().then(() => finish(true), () => finish(false));
  });
  assets.set(src, entry);
  return entry.ready;
}

export async function prepareComicQuestionAssets(quiz: QuizDefinition, question: QuizQuestion): Promise<void> {
  await Promise.all([prepareFonts(quiz.theme.fontFamily), ...sources(quiz, question).map(prepareImage)]);
}

export function comicQuestionAssetsReady(quiz: QuizDefinition, question: QuizQuestion): boolean {
  return fonts.get(quiz.theme.fontFamily)?.settled === true && sources(quiz, question).every((src) => assets.get(src)?.loaded !== undefined);
}

export function comicParchmentAvailable(src: string | undefined): boolean {
  return Boolean(src && assets.get(src)?.loaded);
}
